import React, { useEffect, useState } from "react";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { NAV, PRACTICE, ASSETS } from "@/data/site";
import { useT } from "@/i18n/LanguageContext";
import LanguageToggle from "./LanguageToggle";

const slugId = (s) => s.toLowerCase().replace(/\s+/g, "-");

export default function Navbar() {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMobileExpanded(null);
  }, [location.pathname, location.hash]);

  return (
    <header
      data-testid="navbar"
      className={`sticky top-0 z-40 w-full transition-colors ${
        scrolled
          ? "backdrop-blur-xl bg-[var(--bg)]/90 border-b border-[var(--border)]/70"
          : "bg-[var(--bg)]/50 backdrop-blur-sm"
      }`}
    >
      <nav className="px-6 md:px-10 lg:px-16 py-4 flex items-center justify-between">
        <Link
          to="/"
          data-testid="brand-link"
          className="flex items-center gap-3 group"
        >
          <img
            src={ASSETS.logo}
            alt="Smiles of Loudoun Dentistry"
            className="h-14 w-14 md:h-16 md:w-16 rounded-2xl object-cover shadow-sm"
          />
          <div className="hidden sm:block leading-tight">
            <div className="font-serif-display text-[20px] md:text-[22px] text-[var(--primary)]">
              Smiles <span className="italic text-[var(--accent)]">of</span> Loudoun
            </div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[var(--text-muted)] mt-0.5">
              Dentistry · Leesburg, VA
            </div>
          </div>
        </Link>

        <ul className="hidden lg:flex items-center gap-9 text-[14px] text-[var(--primary)]">
          {NAV.map((n) => {
            const hasChildren = Array.isArray(n.children) && n.children.length > 0;
            return (
              <li key={n.label} className={hasChildren ? "relative group" : ""}>
                <Link
                  data-testid={`nav-${slugId(n.label)}`}
                  to={n.to}
                  className="relative inline-flex items-center gap-1 hover:text-[var(--accent)] after:absolute after:left-0 after:-bottom-1 after:w-0 after:h-px after:bg-[var(--accent)] hover:after:w-full after:transition-all"
                >
                  {t(n.label)}
                  {hasChildren && (
                    <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover:rotate-180" strokeWidth={2} />
                  )}
                </Link>

                {hasChildren && (
                  <div
                    data-testid={`nav-${slugId(n.label)}-submenu`}
                    className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50"
                  >
                    <ul className="min-w-[220px] bg-[var(--bg)] border border-[var(--border)] rounded-2xl shadow-[0_20px_60px_rgb(0,0,0,0.08)] py-2">
                      {n.children.map((c) => (
                        <li key={c.label}>
                          {c.external ? (
                            <a
                              data-testid={`nav-${slugId(n.label)}-sub-${slugId(c.label)}`}
                              href={c.to}
                              target="_blank"
                              rel="noreferrer"
                              className="block px-5 py-2.5 text-[14px] text-[var(--primary)] hover:bg-[var(--surface)] hover:text-[var(--accent)] transition-colors"
                            >
                              {t(c.label)}
                            </a>
                          ) : (
                            <Link
                              data-testid={`nav-${slugId(n.label)}-sub-${slugId(c.label)}`}
                              to={c.to}
                              className="block px-5 py-2.5 text-[14px] text-[var(--primary)] hover:bg-[var(--surface)] hover:text-[var(--accent)] transition-colors"
                            >
                              {t(c.label)}
                            </Link>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        <div className="flex items-center gap-3">
          <div className="hidden md:block">
            <LanguageToggle />
          </div>
          <a
            data-testid="nav-call-btn"
            href={PRACTICE.phoneHref}
            className="hidden md:inline-flex items-center gap-2 text-sm px-4 py-2.5 rounded-full border border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-[var(--bg)]"
          >
            <Phone className="w-3.5 h-3.5" strokeWidth={1.8} /> {PRACTICE.phone}
          </a>
          <div className="hidden md:flex flex-col items-stretch gap-1.5">
            <a
              data-testid="nav-appointment-btn"
              href={PRACTICE.appointmentUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center text-sm px-5 py-2.5 rounded-full bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)]"
            >
              {t("Request Appointment")}
            </a>
            <a
              data-testid="nav-pay-online-btn"
              href={PRACTICE.payOnlineUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-1.5 text-xs px-5 py-2 rounded-full bg-[var(--primary)] text-white hover:bg-[var(--accent)] transition-colors"
            >
              {t("Pay Online")}
            </a>
          </div>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 rounded-full border border-[var(--border)] text-[var(--primary)]"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-[var(--border)] bg-[var(--bg)]"
        >
          <ul className="flex flex-col px-6 py-4 gap-1">
            {NAV.map((n) => {
              const hasChildren = Array.isArray(n.children) && n.children.length > 0;
              const isExpanded = mobileExpanded === n.label;
              return (
                <li key={n.label} className="border-b border-[var(--border)]/70">
                  <div className="flex items-center justify-between">
                    <Link
                      data-testid={`mobile-nav-${slugId(n.label)}`}
                      to={n.to}
                      className="flex-1 py-3 text-[var(--primary)]"
                    >
                      {t(n.label)}
                    </Link>
                    {hasChildren && (
                      <button
                        data-testid={`mobile-nav-${slugId(n.label)}-toggle`}
                        onClick={() => setMobileExpanded(isExpanded ? null : n.label)}
                        className="p-2 text-[var(--primary)]"
                        aria-label={`Toggle ${n.label} submenu`}
                      >
                        <ChevronDown
                          className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                          strokeWidth={2}
                        />
                      </button>
                    )}
                  </div>
                  {hasChildren && isExpanded && (
                    <ul className="pb-3 pl-4">
                      {n.children.map((c) => (
                        <li key={c.label}>
                          {c.external ? (
                            <a
                              data-testid={`mobile-nav-${slugId(n.label)}-sub-${slugId(c.label)}`}
                              href={c.to}
                              target="_blank"
                              rel="noreferrer"
                              className="block py-2 text-[14px] text-[var(--text-muted)] hover:text-[var(--accent)]"
                            >
                              {t(c.label)}
                            </a>
                          ) : (
                            <Link
                              data-testid={`mobile-nav-${slugId(n.label)}-sub-${slugId(c.label)}`}
                              to={c.to}
                              className="block py-2 text-[14px] text-[var(--text-muted)] hover:text-[var(--accent)]"
                            >
                              {t(c.label)}
                            </Link>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
            <li className="pt-3 flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <LanguageToggle />
                <a
                  data-testid="mobile-call-btn"
                  href={PRACTICE.phoneHref}
                  className="flex-1 text-center px-4 py-3 rounded-full border border-[var(--primary)]"
                >
                  {t("Call")}
                </a>
              </div>
              <a
                data-testid="mobile-appointment-btn"
                href={PRACTICE.appointmentUrl}
                target="_blank"
                rel="noreferrer"
                className="text-center px-4 py-3 rounded-full bg-[var(--accent)] text-white"
              >
                {t("Book Visit")}
              </a>
              <a
                data-testid="mobile-pay-online-btn"
                href={PRACTICE.payOnlineUrl}
                target="_blank"
                rel="noreferrer"
                className="text-center px-4 py-3 rounded-full bg-[var(--primary)] text-white"
              >
                {t("Pay Online")}
              </a>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
